'use client';

import type { FC } from 'react';
import { useEffect, useRef } from 'react';
import * as THREE from 'three';

const GLOBE_RADIUS = 20;
const MASK_SAMPLE_STEP = 2;
const MAX_ARCS = 6;
const ARC_POINT_COUNT = 80;
const MASK_W = 1024;
const MASK_H = 512;
const LAND_MASK_SRC = '/textures/land-mask.svg';

const CITIES: Array<{ lat: number; lng: number }> = [
  { lat: 40.7128, lng: -74.006 },
  { lat: 34.0522, lng: -118.2437 },
  { lat: 43.6532, lng: -79.3832 },
  { lat: -23.5505, lng: -46.6333 },
  { lat: 51.5074, lng: -0.1278 },
  { lat: 48.8566, lng: 2.3522 },
  { lat: 52.52, lng: 13.405 },
  { lat: 52.3676, lng: 4.9041 },
  { lat: 25.2048, lng: 55.2708 },
  { lat: 19.076, lng: 72.8777 },
  { lat: 1.3521, lng: 103.8198 },
  { lat: 35.6762, lng: 139.6503 },
  { lat: -33.8688, lng: 151.2093 },
  { lat: 22.3193, lng: 114.1694 },
  { lat: 37.5665, lng: 126.978 },
  { lat: 31.2304, lng: 121.4737 },
  { lat: 6.5244, lng: 3.3792 },
  { lat: 30.0444, lng: 31.2357 },
  { lat: 55.7558, lng: 37.6173 },
  { lat: 25.7617, lng: -80.1918 },
  { lat: 47.6062, lng: -122.3321 },
];

async function loadLandMask(): Promise<ImageData> {
  const canvas = document.createElement('canvas');
  canvas.width = MASK_W;
  canvas.height = MASK_H;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas not supported');

  const image = new Image();
  image.decoding = 'async';
  image.crossOrigin = 'anonymous';

  await new Promise<void>((resolve, reject) => {
    image.onload = () => resolve();
    image.onerror = () => reject(new Error('Failed to load land mask'));
    image.src = LAND_MASK_SRC;
  });

  ctx.clearRect(0, 0, MASK_W, MASK_H);
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(image, 0, 0, MASK_W, MASK_H);

  return ctx.getImageData(0, 0, MASK_W, MASK_H);
}

function latLngToVec3(lat: number, lng: number, r: number): THREE.Vector3 {
  const phi = (90 - lat) * (Math.PI / 180);
  const theta = (lng + 180) * (Math.PI / 180);
  return new THREE.Vector3(
    -r * Math.sin(phi) * Math.cos(theta),
    r * Math.cos(phi),
    r * Math.sin(phi) * Math.sin(theta)
  );
}

// Stripe technique: twinkling dots via custom vertex/fragment shaders
const DOT_VERT = /* glsl */ `
  attribute float aSize;
  attribute float aPhase;
  uniform float uTime;
  varying float vAlpha;
  
  varying float vFresnel;

  void main() {
  vec3 vNormal = normalize(position);
    vec3 viewDir = normalize(cameraPosition - (modelMatrix * vec4(position, 1.0)).xyz);
    vFresnel = pow(dot(vNormal, viewDir), 1.0); // Dots facing camera are 1.0, edges are 0.0
    
    vAlpha = 0.45 + 0.55 * abs(sin(uTime * 0.9 + aPhase));
    vec4 mvPos = modelViewMatrix * vec4(position, 1.0);

    gl_PointSize = aSize * (600.0 / -mvPos.z); 
    gl_Position = projectionMatrix * mvPos;
  }
`;

const DOT_FRAG = /* glsl */ `
  uniform vec3 uColor;
  varying float vAlpha;

  void main() {
    vec2 uv = gl_PointCoord - 0.5;
    float d = length(uv);
    if (d > 0.5) discard;

    // Add a "Light" direction
    vec3 lightDir = normalize(vec3(0.5, 0.5, 1.0));
    // Vary alpha based on vNormal (you'll need to pass vNormal from vertex shader)
    // For now, let's just use your existing alpha
    
    float alpha = vAlpha * smoothstep(0.5, 0.2, d);
    gl_FragColor = vec4(uColor, alpha);
  }
`;

const ARC_VERT = /* glsl */ `
  attribute float aProgress;
  varying float vProgress;

  void main() {
    vProgress = aProgress;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const ARC_FRAG = /* glsl */ `
  uniform vec3 uColor;
  uniform float uOpacity;
  uniform float uDrawProgress; // How much of the line is "born"
  varying float vProgress;

  void main() {
    // This creates the "tail" effect: 
    // The line is brighter at the head (vProgress 1.0) and fades at the tail (vProgress 0.0)
    float tail = pow(vProgress, 3.0); 
    
    // Discard pixels beyond the current draw animation
    if (vProgress > uDrawProgress) discard;

    gl_FragColor = vec4(uColor, tail * uOpacity);
  }
`;

type ArcState = {
  line: THREE.Line;
  material: THREE.ShaderMaterial;
  pointCount: number;
  progress: number;
  speed: number;
  phase: 'grow' | 'hold' | 'shrink';
  holdTimer: number;
};

function spawnArc(scene: THREE.Scene, arcs: ArcState[]): void {
  const a = Math.floor(Math.random() * CITIES.length);
  let b = Math.floor(Math.random() * CITIES.length);
  while (b === a) b = Math.floor(Math.random() * CITIES.length);

  const startCity = CITIES[a];
  const endCity = CITIES[b];
  if (!startCity || !endCity) return;

  const start = latLngToVec3(startCity.lat, startCity.lng, GLOBE_RADIUS);
  const end = latLngToVec3(endCity.lat, endCity.lng, GLOBE_RADIUS);

  // Calculate two control points for a smoother "Stripe-style" arc
  const distance = start.distanceTo(end);
  const midPoint = start.clone().add(end).normalize();
  const height = GLOBE_RADIUS + distance * 0.4; // Arc height relative to distance

  const ctrl1 = start
    .clone()
    .lerp(midPoint, 0.5)
    .normalize()
    .multiplyScalar(height);
  const ctrl2 = end
    .clone()
    .lerp(midPoint, 0.5)
    .normalize()
    .multiplyScalar(height);

  const curve = new THREE.CubicBezierCurve3(start, ctrl1, ctrl2, end);
  const points = curve.getPoints(ARC_POINT_COUNT);

  // Create progress attribute (0 to 1) for the fading tail
  const progressArr = new Float32Array(points.length);
  for (let i = 0; i < points.length; i++) {
    progressArr[i] = i / (points.length - 1);
  }

  const geo = new THREE.BufferGeometry().setFromPoints(points);
  geo.setAttribute('aProgress', new THREE.BufferAttribute(progressArr, 1));

  const mat = new THREE.ShaderMaterial({
    vertexShader: ARC_VERT,
    fragmentShader: ARC_FRAG,
    uniforms: {
      uColor: { value: new THREE.Color(0xff5f33) }, // Vibrant Neon Orange
      uOpacity: { value: 1.0 },
      uDrawProgress: { value: 0.0 },
    },
    transparent: true,
    depthWrite: false,
  });

  const line = new THREE.Line(geo, mat);
  scene.add(line);

  arcs.push({
    line,
    material: mat,
    pointCount: ARC_POINT_COUNT + 1,
    progress: 0,
    speed: 0.4 + Math.random() * 0.3,
    phase: 'grow',
    holdTimer: 0,
  });
}

export const WebGLHero: FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<number>(0);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let disposed = false;
    let scene: THREE.Scene | null = null;
    let camera: THREE.PerspectiveCamera | null = null;
    let renderer: THREE.WebGLRenderer | null = null;
    let landGeo: THREE.BufferGeometry | null = null;
    let landMat: THREE.ShaderMaterial | null = null;
    let cityGeo: THREE.BufferGeometry | null = null;
    let cityMat: THREE.ShaderMaterial | null = null;
    let atmosphereGeo: THREE.SphereGeometry | null = null;
    let atmosphereMat: THREE.ShaderMaterial | null = null;
    let oceanGeo: THREE.SphereGeometry | null = null;
    let oceanMat: THREE.MeshBasicMaterial | null = null;
    let arcs: ArcState[] = [];
    let handleMouseMove: ((e: MouseEvent) => void) | null = null;
    let handleResize: (() => void) | null = null;

    const init = async () => {
      let landMask: ImageData;
      try {
        landMask = await loadLandMask();
      } catch (error) {
        if (!disposed) {
          console.error(error);
        }
        return;
      }
      if (disposed) return;

      scene = new THREE.Scene();
      camera = new THREE.PerspectiveCamera(
        60,
        window.innerWidth / window.innerHeight,
        0.1,
        1000
      );
      camera.position.z = 50;
      camera.position.y = 10;

      // Stripe: antialias: false is the single biggest performance win on HiDPI
      renderer = new THREE.WebGLRenderer({ alpha: true, antialias: false });
      renderer.setSize(window.innerWidth, window.innerHeight);
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      container.appendChild(renderer.domElement);

      // Sample the SVG land mask on a fixed grid for crisp continent definition.
      const landPos: number[] = [];
      const landSz: number[] = [];
      const landPh: number[] = [];

      const jitter = MASK_SAMPLE_STEP * 0.6;
      for (let y = 0; y < MASK_H; y += MASK_SAMPLE_STEP) {
        for (let x = 0; x < MASK_W; x += MASK_SAMPLE_STEP) {
          const rowOffset = y * MASK_W;
          const idx = (rowOffset + x) * 4;
          const alpha = landMask.data[idx + 3] ?? 0;
          const alphaAbove = (dy: number, dx: number) => {
            const ny = y + dy;
            const nx = x + dx;
            if (ny < 0 || ny >= MASK_H || nx < 0 || nx >= MASK_W) return false;
            return (landMask.data[(ny * MASK_W + nx) * 4 + 3] ?? 0) > 20;
          };
          const isLand =
            alpha > 20 ||
            alphaAbove(-1, 0) ||
            alphaAbove(1, 0) ||
            alphaAbove(0, -1) ||
            alphaAbove(0, 1);
          if (!isLand) continue;

          const jitterX =
            (Math.random() - 0.5) * jitter + MASK_SAMPLE_STEP * 0.5;
          const jitterY =
            (Math.random() - 0.5) * jitter + MASK_SAMPLE_STEP * 0.5;
          const px = Math.min(MASK_W - 1, Math.max(0, x + jitterX));
          const py = Math.min(MASK_H - 1, Math.max(0, y + jitterY));

          const lng = (px / MASK_W) * 360 - 180;
          const lat = 90 - (py / MASK_H) * 180;
          const v = latLngToVec3(lat, lng, GLOBE_RADIUS + 0.2);

          landPos.push(v.x, v.y, v.z);
          landSz.push(0.075 + Math.random() * 0.05);
          landPh.push(Math.random() * Math.PI * 2);
        }
      }

      atmosphereGeo = new THREE.SphereGeometry(GLOBE_RADIUS * 1.1, 64, 64);
      atmosphereMat = new THREE.ShaderMaterial({
        transparent: true,
        side: THREE.BackSide, // Render on the "inside" of the sphere
        uniforms: { uColor: { value: new THREE.Color(0x4080ff) } },
        vertexShader: `
    varying vec3 vNormal;
    void main() {
      vNormal = normalize(normalMatrix * normal);
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
        fragmentShader: `
    varying vec3 vNormal;
    uniform vec3 uColor;
    void main() {
      float intensity = pow(0.7 - dot(vNormal, vec3(0, 0, 1.0)), 2.0);
      gl_FragColor = vec4(uColor, intensity * 0.3);
    }
  `,
      });

      const atmosphere = new THREE.Mesh(atmosphereGeo, atmosphereMat);
      scene.add(atmosphere);

      landGeo = new THREE.BufferGeometry();
      landGeo.setAttribute(
        'position',
        new THREE.Float32BufferAttribute(landPos, 3)
      );
      landGeo.setAttribute(
        'aSize',
        new THREE.Float32BufferAttribute(landSz, 1)
      );
      landGeo.setAttribute(
        'aPhase',
        new THREE.Float32BufferAttribute(landPh, 1)
      );

      const landUniforms = {
        uTime: { value: 0 },
        uColor: { value: new THREE.Color(0xd1e5ff) }, // Ensure this is pure white
      };
      landMat = new THREE.ShaderMaterial({
        vertexShader: DOT_VERT,
        fragmentShader: DOT_FRAG,
        uniforms: landUniforms,
        transparent: true,
        depthTest: true,
        depthWrite: false,
      });
      const land = new THREE.Points(landGeo, landMat);
      land.renderOrder = 2;
      scene.add(land);

      oceanGeo = new THREE.SphereGeometry(GLOBE_RADIUS * 0.98, 64, 64);
      oceanMat = new THREE.MeshBasicMaterial({
        color: 0x010409,
        transparent: false, // Make it solid to hide back-side dots
        side: THREE.FrontSide,
      });

      const ocean = new THREE.Mesh(oceanGeo, oceanMat);
      ocean.renderOrder = 1; // Lower number renders first
      scene.add(ocean);

      // City dots — accent color
      const cityPos: number[] = [];
      const citySz: number[] = [];
      const cityPh: number[] = [];
      CITIES.forEach((c) => {
        const v = latLngToVec3(c.lat, c.lng, GLOBE_RADIUS * 1.02); // 1.02 gives a nice hover
        cityPos.push(v.x, v.y, v.z);
        citySz.push(0.5 + Math.random() * 0.2);
        cityPh.push(Math.random() * Math.PI * 2);
      });
      cityGeo = new THREE.BufferGeometry();
      cityGeo.setAttribute(
        'position',
        new THREE.Float32BufferAttribute(cityPos, 3)
      );
      cityGeo.setAttribute(
        'aSize',
        new THREE.Float32BufferAttribute(citySz, 1)
      );
      cityGeo.setAttribute(
        'aPhase',
        new THREE.Float32BufferAttribute(cityPh, 1)
      );
      const cityUniforms = {
        uTime: { value: 0 },
        uColor: { value: new THREE.Color(0xffa500) }, // Gold/Orange
      };
      cityMat = new THREE.ShaderMaterial({
        vertexShader: DOT_VERT,
        fragmentShader: DOT_FRAG,
        uniforms: cityUniforms,
        transparent: true,
        depthWrite: false,
      });
      scene.add(new THREE.Points(cityGeo, cityMat));

      // Arc animations
      arcs = [];
      for (let i = 0; i < 3; i++) spawnArc(scene, arcs);

      let mouseX = 0;
      let mouseY = 0;
      handleMouseMove = (e: MouseEvent) => {
        mouseX = (e.clientX / window.innerWidth - 0.5) * 2;
        mouseY = (e.clientY / window.innerHeight - 0.5) * 2;
      };
      document.addEventListener('mousemove', handleMouseMove);

      let elapsed = 0;
      let lastArcSpawn = 0;
      let prevTime = performance.now();

      const animate = () => {
        animationRef.current = requestAnimationFrame(animate);
        if (!scene || !camera || !renderer) return;
        const now = performance.now();
        const delta = Math.min((now - prevTime) / 1000, 0.05);
        prevTime = now;
        elapsed += delta;

        const targetRotationY = elapsed * 0.1 + mouseX * 0.5;
        scene.rotation.y += (targetRotationY - scene.rotation.y) * 0.05;

        landUniforms.uTime.value = elapsed;
        cityUniforms.uTime.value = elapsed;

        for (let i = arcs.length - 1; i >= 0; i--) {
          const arc = arcs[i];
          if (
            arc &&
            arc.material &&
            arc.material.uniforms.uDrawProgress &&
            arc.phase === 'grow'
          ) {
            arc.progress += delta * arc.speed;
            arc.material.uniforms.uDrawProgress.value = arc.progress;

            if (arc.progress >= 1) {
              arc.phase = 'hold';
            }
          } else if (arc && arc.phase === 'hold') {
            arc.holdTimer += delta;
            if (arc.holdTimer > 1.2) arc.phase = 'shrink';
          } else if (arc && arc.material && arc.material.uniforms.uOpacity) {
            arc.material.uniforms.uOpacity.value -= delta * 1.1;
            if (arc.material.uniforms.uOpacity.value <= 0) {
              scene.remove(arc.line);
              arc.line.geometry.dispose();
              arc.material.dispose();
              arcs.splice(i, 1);
            }
          }
        }

        if (elapsed - lastArcSpawn > 1.6 && arcs.length < MAX_ARCS) {
          spawnArc(scene, arcs);
          lastArcSpawn = elapsed;
        }

        renderer.render(scene, camera);
      };
      animate();

      handleResize = () => {
        if (!camera || !renderer) return;
        camera.aspect = window.innerWidth / window.innerHeight;
        camera.updateProjectionMatrix();
        renderer.setSize(window.innerWidth, window.innerHeight);
      };
      window.addEventListener('resize', handleResize);
    };

    init();

    return () => {
      disposed = true;
      cancelAnimationFrame(animationRef.current);
      if (handleMouseMove) {
        document.removeEventListener('mousemove', handleMouseMove);
      }
      if (handleResize) {
        window.removeEventListener('resize', handleResize);
      }
      if (renderer) {
        renderer.dispose();
        if (container.contains(renderer.domElement)) {
          container.removeChild(renderer.domElement);
        }
      }
      if (landGeo) landGeo.dispose();
      if (landMat) landMat.dispose();
      if (cityGeo) cityGeo.dispose();
      if (cityMat) cityMat.dispose();
      if (atmosphereGeo) atmosphereGeo.dispose();
      if (atmosphereMat) atmosphereMat.dispose();
      if (oceanGeo) oceanGeo.dispose();
      if (oceanMat) oceanMat.dispose();
      if (scene) {
        arcs.forEach((arc) => {
          scene?.remove(arc.line);
          arc.line.geometry.dispose();
          arc.material.dispose();
        });
      }
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 -z-10 size-full"
      style={{
        background:
          'radial-gradient(circle at center, #020c1b 0%, #010409 100%)',
      }}
    />
  );
};
